import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Input from "../../components/Input";
import InputRadio from "../../components/InputRadio";
import InputImage from "../../components/InputImage";
import InputColor from "../../components/InputColor";
import PageFooter from "../../components/PageFooter";
import { Loading } from "../../components/Loading";
import SelectCompany from "../../components/selects/SelectCompany";
import Icon from "../../components/Icon";
import SubSidebar from "../../components/SubSidebar";
import BrandHeader from "../../components/BrandHeader";
import BannersTab from "./BannersTab";
import ProductsTab from "./ProductsTab";
import CustomersTab from "./CustomersTab";
import SeoTab from "./SeoTab";
import DomainTab from "./DomainTab";
import PagesTab from "./PagesTab";
import ContactTab from "./ContactTab";
import BrandBreadcrumb from "./BrandBreadcrumb";

import api from "../../services/api";
import { canCreateBrands, canEditBrands, getData, hasPlanFeature } from "../../services/auth";
import { renderError } from "../../helpers/errors";
import {
    buildBrandUrl,
    generateBrandSlug,
    sanitizeUrlValue,
    getBrandsProvisionalDomain,
    getUrlValueMaxLength,
    resolveImageUrl
} from "../../helpers/brand";

const accessModeOptions = [
    { label: 'Qualquer pessoa', value: 'public' },
    { label: 'Somente pessoas pré-cadastradas', value: 'restricted' }
];

const priceDisplayOptions = [
    { label: 'Qualquer pessoa', value: 'full_view' },
    { label: 'Somente quem estiver logado', value: 'all_can_view' },
    { label: 'Ninguém', value: 'noshow' }
];

const priceDisplayHints = {
    full_view: 'Quem abrir o site da marca já vê os preços do catálogo.',
    all_can_view: 'O catálogo fica aberto, mas o preço só aparece depois que a pessoa entra com o cadastro dela.',
    noshow: 'O site da marca não mostra preços: a pessoa monta o pedido e pede um orçamento.'
};

// Marca nova nasce com a regra do site principal. O modo por aprovação do
// tenant não existe na marca — lá a aprovação é o "pode ver preços" da pessoa,
// que o painel só edita quando o site principal está nesse modo.
const inheritedPriceDisplayOption = () => {
    const option = getData()?.price_display_option;
    return priceDisplayOptions.some(item => item.value === option) ? option : 'noshow';
};

const tabs = [
    { key: 'overview', name: 'Visão geral', svg: 'dashboard' },
    { key: 'theme', name: 'Tema e identidade', svg: 'design' },
    { key: 'domain', name: 'Domínio', svg: 'link' },
    { key: 'users', name: 'Pessoas', svg: 'people' },
    { key: 'products', name: 'Produtos', svg: 'product' },
    // A paleta ficou com "Tema e identidade"; banners viram a imagem.
    { key: 'banners', name: 'Banners', svg: 'image' },
    { key: 'seo', name: 'SEO', svg: 'search' },
    { key: 'pages', name: 'Páginas', svg: 'pdf' },
    { key: 'contact', name: 'Contato e mídias', svg: 'contact' }
];

// Pessoas, produtos, banners, páginas e domínio salvam na hora; o "Salvar
// alterações" do rodapé só tem o que salvar nas abas de formulário.
const FORM_TABS = ['overview', 'theme', 'seo', 'contact'];

const emptySeo = { title: '', description: '', og_title: '', og_description: '' };

export const BrandEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const isCreate = !id;
    const canSave = isCreate ? canCreateBrands() : canEditBrands();

    const [loading, setLoading] = useState(!isCreate);
    const [saving, setSaving] = useState(false);
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get('tab') ?? 'overview');

    const [brand, setBrand] = useState(null);

    // Informações gerais
    const [name, setName] = useState('');
    const [companyId, setCompanyId] = useState(null);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [nameTouched, setNameTouched] = useState(false);
    const [active, setActive] = useState(true);
    const [accessMode, setAccessMode] = useState('public');
    const [priceDisplayOption, setPriceDisplayOption] = useState(inheritedPriceDisplayOption);

    // A marca nasce no endereço provisório (*.marcas.brinde.me) para o cliente
    // ver o site no mesmo dia; o domínio próprio é configurado na aba Domínio.
    const [urlValue, setUrlValue] = useState('');
    const [urlTouched, setUrlTouched] = useState(false);

    // Tema e identidade
    const [theme, setTheme] = useState(null);
    const [themeOptions, setThemeOptions] = useState([]);
    const [siteLogoBase64, setSiteLogoBase64] = useState('');
    const [siteLogoPreview, setSiteLogoPreview] = useState(null);
    const [faviconBase64, setFaviconBase64] = useState('');
    const [faviconPreview, setFaviconPreview] = useState(null);
    const [primaryColor, setPrimaryColor] = useState('#F5A623');
    const [secondaryColor, setSecondaryColor] = useState('#D81B60');
    const [tertiaryColor, setTertiaryColor] = useState('#C2185B');

    // SEO e contato: campos da própria marca, salvos junto do resto.
    const [seo, setSeo] = useState(emptySeo);
    const [ogImageBase64, setOgImageBase64] = useState('');
    const [ogImagePreview, setOgImagePreview] = useState(null);
    const [contact, setContact] = useState({});

    useEffect(() => {
        if (!isCreate) getBrand();
    }, [id]);

    // Temas de marca vêm do backend (themes.scope = brand): cada um é um
    // projeto na Vercel, e é pelo tema que o host da marca é roteado.
    useEffect(() => {
        api.get(`/brand/themes`).then(res => {
            const options = res.data.resources ?? [];
            setThemeOptions(options);
            setTheme(current => current ?? (options.find(option => option.default) ?? options[0])?.id ?? null);
        }).catch(error => renderError(error));
    }, []);

    // Sugestão automática do endereço a partir do nome: SÓ na criação.
    useEffect(() => {
        if (isCreate && !urlTouched) {
            setUrlValue(generateBrandSlug(name));
        }
    }, [name]);

    const selectedCompany = companyOptions.find(company => company.id === companyId) ?? null;

    // Escolher a empresa preenche o nome da marca — e o nome, por tabela, gera o
    // subdomínio no efeito acima. Vale só enquanto o usuário não escreveu nada:
    // depois disso o que ele digitou manda, inclusive na troca de empresa.
    useEffect(() => {
        if (nameTouched || !selectedCompany) return;
        setName(selectedCompany.trade_name ?? '');
    }, [selectedCompany]);

    const handleNameChange = value => {
        setNameTouched(true);
        setName(value);
    };

    const getBrand = () => {
        setLoading(true);

        api.get(`/brand/${id}`).then(res => {
            const resource = res.data.resource ?? res.data.resources;
            fillBrand(resource);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    const fillBrand = resource => {
        if (!resource) return;

        setBrand(resource);
        setName(resource.name ?? '');
        setNameTouched(true);
        setCompanyId(resource.company_id ?? resource.company?.id ?? null);
        setActive(resource.active ?? true);
        setAccessMode(resource.access_mode ?? 'public');
        setPriceDisplayOption(resource.price_display_option ?? 'noshow');

        setTheme(resource.theme_id ?? null);
        setFaviconPreview(resolveImageUrl(resource.favicon_url));
        setSiteLogoPreview(resolveImageUrl(resource.site_logo_url));
        setPrimaryColor(resource.primary_color ?? '#F5A623');
        setSecondaryColor(resource.secondary_color ?? '#D81B60');
        setTertiaryColor(resource.tertiary_color ?? '#C2185B');

        setSeo({
            title: resource.seo?.title ?? '',
            description: resource.seo?.description ?? '',
            og_title: resource.seo?.og?.title ?? '',
            og_description: resource.seo?.og?.description ?? ''
        });
        setOgImagePreview(resource.seo?.og?.image ?? null);
        setOgImageBase64('');
        setContact(stripEmpty(resource.contact));
    };

    // O backend devolve toda chave de contato, preenchida ou null; o form só
    // guarda o que tem valor.
    const stripEmpty = object => Object.fromEntries(
        Object.entries(object ?? {}).filter(([, value]) => value != null && value !== '')
    );

    const handleUrlValueChange = value => {
        setUrlTouched(true);
        setUrlValue(sanitizeUrlValue(false, value).slice(0, getUrlValueMaxLength(false)));
    };

    const saveBrand = () => {
        setSaving(true);


        const data = {
            company_id: companyId,
            name,
            slug: generateBrandSlug(name) || urlValue,
            theme_id: theme,
            primary_color: primaryColor,
            secondary_color: secondaryColor,
            tertiary_color: tertiaryColor,
            access_mode: accessMode,
            price_display_option: priceDisplayOption,
            active
        };

        // O endereço só vai na criação: na edição ele tem fluxo próprio, na aba
        // Domínio. Opcional e único: só envia quando há valor.
        if (isCreate && urlValue) data.domain = buildBrandUrl(false, urlValue).replace(/^https:\/\//, '');

        // Imagens como data URL base64; só envia quando trocadas (vazio mantém atual)
        if (faviconBase64) data.favicon_path = faviconBase64;
        if (siteLogoBase64) data.site_logo = siteLogoBase64;

        // SEO e contato só existem na edição: na criação as abas não aparecem.
        if (!isCreate) {
            data.seo_title = seo.title;
            data.seo_description = seo.description;
            data.seo_og_title = seo.og_title;
            data.seo_og_description = seo.og_description;
            data.contact = contact;
            if (ogImageBase64) data.seo_og_image = ogImageBase64;
        }

        const request = isCreate
            ? api.post(`/brand`, data)
            : api.put(`/brand/${id}`, data);

        request.then(res => {
            if (isCreate) {
                toast.success('Marca cadastrada com sucesso');
                const created = res.data.resource ?? res.data.resources;
                if (created?.id) {
                    navigate(`/brand/edit/${created.id}`);
                } else {
                    navigate(`/brand/list`);
                }
            } else {
                toast.success('Marca atualizada com sucesso');
                getBrand();
            }
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    };

    const siteUrl = isCreate
        ? buildBrandUrl(false, urlValue)
        : (brand?.domain ? `https://${brand.domain}` : '');

    const siteLogo = siteLogoBase64 || siteLogoPreview
        ? <img src={siteLogoBase64 || siteLogoPreview} alt="" />
        : <span className="brand-preview-wordmark">{name || 'Sua marca'}</span>;

    // Vênus (template_1): primária na tarja do topo, no menu e nos ícones de
    // vantagens, secundária no botão do card.
    const renderVenusPreview = () => (
        <div className="brand-preview-site">
            <div className="brand-preview-topbar" style={{ background: primaryColor }} />

            <div className="brand-preview-header">
                {siteLogo}
                <span className="brand-preview-search">O que você procura?</span>
                <span className="brand-preview-icons">
                    <span /><span />
                </span>
            </div>

            <div className="brand-preview-menu" style={{ background: primaryColor }}>
                {[0, 1, 2, 3].map(i => <span key={i} />)}
            </div>

            <div className="brand-preview-banner">Banner</div>

            <div className="brand-preview-features">
                {[0, 1, 2, 3].map(i => (
                    <div className="brand-preview-feature" key={i}>
                        <span className="brand-preview-feature-icon" style={{ background: primaryColor }} />
                        <span className="brand-preview-line is-xs" />
                    </div>
                ))}
            </div>

            <div className="brand-preview-grid">
                {[0, 1, 2].map(i => (
                    <div className="brand-preview-card" key={i}>
                        <span className="brand-preview-thumb" />
                        <span className="brand-preview-line is-sm" />
                        <span className="brand-preview-button" style={{ background: secondaryColor }} />
                    </div>
                ))}
            </div>
        </div>
    );

    // Marte (brand_template_2): header branco, catálogo na raiz com coluna de
    // categorias, sem banner. A primária só aparece em preenchimento pequeno
    // (contador do carrinho, barra da categoria ativa, selo Novo); a
    // secundária e a terciária não aparecem em nada visível no tema.
    const renderMarsPreview = () => (
        <div className="brand-preview-site">
            <div className="brand-preview-header is-mars">
                {siteLogo}
                <span className="brand-preview-search is-pill">O que você procura?</span>
                <span className="brand-preview-icons">
                    <span />
                    <span className="brand-preview-cart">
                        <span className="brand-preview-cart-badge" style={{ background: primaryColor }} />
                    </span>
                </span>
            </div>

            <div className="brand-preview-catalog">
                <div className="brand-preview-categories">
                    {[0, 1, 2, 3, 4].map(i => (
                        <span key={i} className={`brand-preview-category${i === 0 ? ' active' : ''}`}>
                            {i === 0 && <span className="brand-preview-category-bar" style={{ background: primaryColor }} />}
                            <span className="brand-preview-line is-xs" />
                        </span>
                    ))}
                </div>

                <div className="brand-preview-grid is-mars">
                    {[0, 1, 2, 3, 4, 5].map(i => (
                        <div className="brand-preview-card is-mars" key={i}>
                            <span className="brand-preview-thumb">
                                {i === 0 && <span className="brand-preview-badge" style={{ background: primaryColor }} />}
                            </span>
                            <span className="brand-preview-card-body">
                                <span className="brand-preview-line is-sm" />
                                <span className="brand-preview-line is-price" />
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const isMarsTheme = themeOptions.find(option => option.id === theme)?.slug === 'brand-2';

    // Preview ao vivo: o cliente está configurando um site, então mostrar o site
    // vale mais do que descrever os campos. Acompanha o tema selecionado, mesmo
    // antes do Salvar.
    const renderPreview = () => (
        <div className="brand-preview">
            <div className="brand-preview-window">
                <div className="brand-preview-chrome">
                    <span className="dot" /><span className="dot" /><span className="dot" />
                    <div className="brand-preview-address">
                        {faviconBase64 || faviconPreview
                            ? <img className="brand-preview-favicon" src={faviconBase64 || faviconPreview} alt="" />
                            : <span className="brand-preview-favicon is-empty" />
                        }
                        <span>{(siteUrl || buildBrandUrl(false, 'minhamarca')).replace(/^https:\/\//, '')}</span>
                    </div>
                </div>

                {isMarsTheme ? renderMarsPreview() : renderVenusPreview()}
            </div>

            <p className="brand-preview-note">
                {isMarsTheme
                    ? 'Prévia aproximada, com a estrutura do tema. Os produtos você configura depois de cadastrar a marca.'
                    : 'Prévia aproximada, com a estrutura do tema. O banner e os produtos você configura depois de cadastrar a marca.'}
            </p>
        </div>
    );

    const nameField = (
        <Input type={`text`} label={`Nome da marca *`} change={handleNameChange} value={name} />
    );

    const addressField = (
        <div className="brand-url-field">
        <Input
            type={`text`}
            label={`Endereço *`}
            change={handleUrlValueChange}
            value={urlValue}
            right={<span className="brand-url-suffix">.{getBrandsProvisionalDomain()}</span>}
        />
        </div>
    );

    const addressHint = (
        <p className="section-hint">
            Domínio provisório. Você poderá alterar depois de cadastrar a marca.
        </p>
    );

    const accessSection = (
        <section className="brand-section">
            <h2 className="section-title">Quem pode acessar?</h2>
            <InputRadio
                options={accessModeOptions}
                value={accessMode}
                change={setAccessMode}
            />
            {accessMode === 'restricted' &&
                <p className="section-hint">
                    Só quem estiver vinculado à marca consegue entrar no site.{' '}
                    {isCreate
                        ? 'As pessoas são cadastradas depois, na aba Pessoas.'
                        : <button type="button" className="brand-inline-link" onClick={() => setTab('users')}>Gerenciar pessoas</button>
                    }
                </p>
            }
        </section>
    );

    // Exibir preço é feature de plano: sem ela o site da marca não mostra preço
    // nenhum, e o rádio não decidiria nada.
    const priceSection = hasPlanFeature('price_display') && (
        <section className="brand-section">
            <h2 className="section-title">Quem vê os preços?</h2>
            <InputRadio
                options={priceDisplayOptions}
                value={priceDisplayOption}
                change={setPriceDisplayOption}
            />
            <p className="section-hint">{priceDisplayHints[priceDisplayOption]}</p>
        </section>
    );

    // A seção de tema vive na sua própria aba na edição, mas na criação ela
    // aparece junto do resto: lá não há abas.
    const themeSection = (
        <>
            <section className="brand-section">
                <h2 className="section-title">Tema e identidade</h2>
                {themeOptions.length > 1 &&
                    <div className="brand-theme-options" role="radiogroup" aria-label="Tema">
                        {themeOptions.map(option => (
                            <button
                                key={option.id}
                                type="button"
                                role="radio"
                                aria-checked={option.id === theme}
                                className={`brand-theme-option${option.id === theme ? ' active' : ''}`}
                                onClick={() => setTheme(option.id)}
                            >
                                <span className="brand-theme-image">
                                    {option.image_url
                                        ? <img src={option.image_url} alt="" />
                                        : <span className="brand-theme-image-placeholder">{option.name.substring(0, 1)}</span>
                                    }
                                </span>
                                <span className="brand-theme-body">
                                    <span className="brand-theme-radio" />
                                    <span>
                                        <strong>{option.name}</strong>
                                        {option.description && <small>{option.description}</small>}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                }
                <div className="row brand-image-row">
                    <div className="col-3">
                        <InputImage
                            label={`Logo do site`}
                            src={siteLogoBase64 || siteLogoPreview}
                            changeBase64={setSiteLogoBase64}
                            onRemove={() => setSiteLogoPreview(null)}
                            hint={`400px por 140px, fundo transparente`}
                        />
                    </div>
                    <div className="col-2 brand-image-col-narrow">
                        <InputImage
                            label={`Favicon`}
                            src={faviconBase64 || faviconPreview}
                            changeBase64={setFaviconBase64}
                            onRemove={() => setFaviconPreview(null)}
                            hint={`48px por 48px`}
                            formats={['png', 'jpeg', 'svg']}
                            square={true}
                        />
                    </div>
                </div>
                <div className="row brand-color-row">
                    <div className="col-4">
                        <InputColor label={`Cor primária`} value={primaryColor} change={setPrimaryColor} />
                    </div>
                    <div className="col-4">
                        <InputColor label={`Cor secundária`} value={secondaryColor} change={setSecondaryColor} />
                    </div>
                    <div className="col-4">
                        <InputColor label={`Cor terciária`} value={tertiaryColor} change={setTertiaryColor} />
                    </div>
                </div>
            </section>
        </>
    );

    const renderOverview = () => (
        <div className="brand-overview">
            <section className="brand-company-step">
                <div className="brand-company-icon">
                    <Icon name="company" size={22} />
                </div>
                <div className="brand-company-body">
                    <h2 className="section-title">{isCreate ? 'Comece pela empresa' : 'Empresa vinculada'}</h2>
                    <p className="section-hint">
                        Os orçamentos realizados no site dessa marca serão vinculados a essa
                        empresa quando chegarem no menu Orçamentos.
                    </p>
                    <div className="brand-company-select">
                        <SelectCompany
                            label={`Empresa vinculada *`}
                            selectedCompany={companyId}
                            setSelectedCompany={setCompanyId}
                            changeCompanies={setCompanyOptions}
                            fields={['trade_name']}
                            minSearch={3}
                        />
                    </div>
                </div>
            </section>

            <section className="brand-section">
                <h2 className="section-title">{isCreate ? 'A marca' : 'Informações gerais'}</h2>
                {/* Na criação nome e endereço andam juntos: o endereço nasce do
                    nome, e cada um sozinho em sua seção deixava a tela vazia. */}
                {isCreate &&
                    <>
                        <div className="row">
                            <div className="col-6">{nameField}</div>
                            <div className="col-6">{addressField}</div>
                        </div>
                        {addressHint}
                    </>
                }
                {!isCreate &&
                    <div className="row">
                        <div className="col-4">{nameField}</div>
                    </div>
                }
            </section>

            {accessSection}

            {priceSection}

            {isCreate && themeSection}
        </div>
    );

    const renderTheme = () => (
        <div className="brand-overview">
            {themeSection}
        </div>
    );

    const renderTabContent = () => {
        switch (tab) {
            case 'theme': return renderTheme();
            case 'domain': return <DomainTab brand={brand} canEdit={canSave} onChange={setBrand} />;
            case 'users': return <CustomersTab brandId={id} companyId={brand?.company_id ?? brand?.company?.id} accessMode={brand?.access_mode} />;
            case 'products': return (
                <ProductsTab
                    brandId={id}
                    brandName={name}
                    catalogMode={brand?.catalog_mode}
                    onCatalogModeChange={mode => setBrand(prev => prev ? { ...prev, catalog_mode: mode } : prev)}
                />
            );
            // Banners: gerência completa (modelo da página de banners), porém sem
            // buscar dados até o endpoint existir — ver guard em BannersTab.
            case 'banners': return <BannersTab brandId={id} theme={themeOptions.find(option => option.id === theme)} />;
            case 'seo': return (
                <SeoTab
                    seo={seo}
                    onChange={setSeo}
                    ogImageBase64={ogImageBase64}
                    setOgImageBase64={setOgImageBase64}
                    ogImagePreview={ogImagePreview}
                    setOgImagePreview={setOgImagePreview}
                    faviconUrl={faviconBase64 || faviconPreview}
                    siteUrl={siteUrl}
                />
            );
            case 'pages': return <PagesTab brandId={id} />;
            case 'contact': return <ContactTab contact={contact} onChange={setContact} />;
            default: return renderOverview();
        }
    };

    return (
        <PanelTemplate id="brand-edit">
            <Loading show={loading} />
            {!loading &&
                <>
                    {/* A criação vive numa coluna de largura fixa: sem o teto, o
                        formulário estica com o monitor e cada campo acaba com uma
                        medida diferente. O cabeçalho acompanha a mesma coluna. */}
                    <div className={isCreate ? 'brand-create-page' : ''}>
                    <BrandBreadcrumb items={[
                        { label: 'Marcas', to: '/brand/list' },
                        { label: isCreate ? 'Nova marca' : (brand?.name ?? 'Marca') }
                    ]} />

                    {isCreate
                        ? <h1 className="brand-page-title">Nova marca</h1>
                        : <BrandHeader
                            logo={faviconBase64 || faviconPreview}
                            name={brand?.name}
                            active={brand?.active}
                            siteUrl={siteUrl}
                            companyName={brand?.company?.trade_name}
                            customersCount={brand?.customers_count ?? brand?.companies_count}
                            onCustomersClick={() => setTab('users')}
                        />
                    }

                    {/* Na criação não há abas (pessoas, produtos e banners dependem
                        da marca existir), então a navegação lateral daria lugar a uma
                        lista de um item só. O espaço vai para o preview. */}
                    {isCreate
                        ? <div className="brand-create">
                            <Box className="brand-create-form">
                                {renderOverview()}
                            </Box>
                            <aside className="brand-create-aside">
                                {renderPreview()}
                            </aside>
                        </div>
                        : <div className="brand-content">
                            <SubSidebar
                                className="brand-tabs"
                                items={tabs}
                                active={tab}
                                onChange={setTab}
                            />
                            <Box className={`brand-tab-body`}>
                                {renderTabContent()}
                            </Box>
                        </div>
                    }

                    {/* Na criação o rodapé acompanha a largura do box do formulário:
                        o botão alinhado à borda dele, não à da página. */}
                    {(isCreate || FORM_TABS.includes(tab)) &&
                        <div className={isCreate ? 'brand-create-footer' : ''}>
                            <PageFooter>
                                {canSave &&
                                    <Button type={`primary`} size={`small`} svg={`save-white`} text={isCreate ? 'Cadastrar marca' : 'Salvar alterações'} action={saveBrand} loading={saving} />
                                }
                            </PageFooter>
                        </div>
                    }
                    </div>
                </>
            }
        </PanelTemplate>
    );
};

export default BrandEdit;
